import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { delay } from 'lodash';
import Confetti from 'react-dom-confetti';
import OrganizationResync from '../organization-resync';
import CRMInstanceName from '../crm-instance-name';
import CheckOrganizationSyncPermissions from '../check-organization-sync-permissions';
import ImportData from '../import-data';

// Import Selectors
import {
  getUser, getIsUsingOrganizationSwitch, getUserCurrentOrganization,
  getCanSetupOrganization,
} from '../../modules/Auth/AuthReducer';
import SyncProgress from './progress';
import { getShouldShowSyncToast } from '../../modules/App/AppReducer';

function r(min, max) {
  return (Math.random() * (max - min)) + min;
}

function getConfig() {
 return {
    angle: 90,
    spread: r(50, 70),
    elementCount: r(50, 100),
    startVelocity: 40,
    dragFriction: 0.12,
    duration: 4000,
    stagger: 3,
    width: '10px',
    height: '10px',
    perspective: '500px',
    colors: ['#a864fd', '#29cdff', '#78ff44', '#ff718d', '#fdff6a'],
  };
}

export default (ComposedComponent) => {
  class RequiredSync extends Component {
    constructor(props) {
      super(props);
      const { pathname } = props.location;
      this.state = {
        playConfetti: false,
        confetti: {},
        pathname,
        disableAutoSync: false,
      };

      this.handleAutoSyncError = this.handleAutoSyncError.bind(this);

      delay(() => {
        if (props.organization && !props.organization.last_synced_date && !props.organization.syncing && props.canSetup) {
          this.setState({ playConfetti: true, confetti: getConfig() });
        }
      }, 1000);
    }

    handleAutoSyncError() {
      this.setState({ disableAutoSync: true });
    }

    renderManualSync() {
      const { organization } = this.props;
      if (!organization.manual_sync) {
        return;
      }
      return (
        <div className="row">
          <div className="col-xs-12 col-sm-12 col-md-10 col-md-push-1 col-lg-8 col-lg-push-2">
            <hr />
            <h3>Manual Sync</h3>
            <ImportData chunks={true} />
            <div style={{ height: '100px'}} />
          </div>
        </div>
      );
    }

    renderContent() {
      const {
        canSetup, organization,
        shouldShowSyncToast,
      } = this.props;

      const lastSyncedDate = organization ? organization.last_synced_date : false;
      const syncing = organization ? organization.syncing : false;

      if (!lastSyncedDate) {
        if (organization.is_demo) {
          return ((
            <div className="container text-center">
              <h1 className="page-header">Welcome to TruVoice Automation</h1>
              <div className="row">
                <div className="col-xs-12 col-sm-12 col-md-10 col-md-push-1 col-lg-8 col-lg-push-2">
                  <p>
                    This is a demo organization without data, please sync the original organization.
                  </p>
                </div>
              </div>
            </div>
          ));
        }

        if (!canSetup) {
          return ((
            <div className="container text-center">
              <h1 className="page-header">Welcome to TruVoice Automation</h1>
              <div className="row">
                <div className="col-xs-12 col-sm-12 col-md-10 col-md-push-1 col-lg-8 col-lg-push-2">
                  <p>
                    TruVoice Automation needs an active user with appropriate rights to sync your <CRMInstanceName /> data with TruVoice Automation.
                  </p>
                  <p>
                    Contact your TruVoice by Corporate Visions sales representative, or <a href="mailto:support@truvoice.io">support@truvoice.io</a> to request support in completing the setup required to use TruVoice Automation.
                  </p>
                </div>
              </div>
            </div>
          ));
        }

        if (!syncing) {
          return (
            <div className="container text-center">
              <div style={{ width: '1px', margin: 'auto' }}>
                <Confetti active={this.state.playConfetti} config={this.state.confetti} />
              </div>
              <h1 className="page-header">
                Welcome to TruVoice Automation
              </h1>
              <div className="row">
                <div className="col-xs-12 col-sm-12 col-md-10 col-md-push-1 col-lg-8 col-lg-push-2">
                  <CheckOrganizationSyncPermissions
                    organization={organization}
                    showSetupForAzureonError
                  >
                    <div>
                      <p>
                        We need to sync your data with TruVoice Automation.
                        Click the button below to begin synchronization process.
                      </p>
                      <p>
                        After the synchronization has completed you will need to setup two areas for your organization
                        before you can begin using TruVoice Automation.
                      </p>
                      <p>
                        Don&rsquo;t worry though. Setting up these two areas typically takes less than 5 minutes for most organizations!
                      </p>
                      <OrganizationResync
                        showConfirm={false}
                        isInitial
                        className="btn btn-primary btn-lg"
                        organization={organization}
                        btnTitle="Start Syncing Data"
                        autoSync={!this.state.disableAutoSync && !organization.last_synced_start_date}
                        onError={this.handleAutoSyncError}
                      />
                    </div>
                    <div style={{ height: '100px'}} />
                  </CheckOrganizationSyncPermissions>
                </div>
              </div>
              {this.renderManualSync()}
            </div>
          );
        }
      }


      if (!syncing) {
        return (<ComposedComponent {... this.props} key="mainComp" />);
      }

      // syncing
      if (!shouldShowSyncToast) {
        return <SyncProgress />;
      }

      if (lastSyncedDate !== null && lastSyncedDate !== undefined) {
        // already synced once
        return <ComposedComponent {... this.props} key="mainComp" />;
      }

      // never sync
      return <SyncProgress />;
    }


    render() {
      const { organization } = this.props;
      if (!organization) {
        return null;
      }
      const orgId = organization._id;
      return (
        <div key={orgId}>
          {this.renderContent()}
        </div>
      );
    }
  }

  // Retrieve data from store as props
  function mapStateToProps(state) {
    return {
      user: getUser(state),
      organization: getUserCurrentOrganization(state),
      canSetup: getCanSetupOrganization(state),
      isUsingOrganizationSwitch: getIsUsingOrganizationSwitch(state),
      shouldShowSyncToast: getShouldShowSyncToast(state),
    };
  }

  RequiredSync.propTypes = {
    dispatch: PropTypes.func.isRequired,
    user: PropTypes.shape({
      user_id: PropTypes.string,
    }).isRequired,
    canSetup: PropTypes.bool.isRequired,
    organization: PropTypes.shape({
      _id: PropTypes.string,
      syncing: PropTypes.bool,
      is_demo: PropTypes.bool,
      last_synced_date: PropTypes.string,
      last_synced_start_date: PropTypes.string,
    }),
    isUsingOrganizationSwitch: PropTypes.bool,
    location: PropTypes.shape({
      pathname: PropTypes.string,
    }).isRequired,
    shouldShowSyncToast: PropTypes.bool,
  };

  RequiredSync.defaultProps = {
    shouldShowSyncToast: false,
  };

  RequiredSync.contextTypes = {
  };

  return withRouter(connect(mapStateToProps)(RequiredSync));
}
